// src/pages/porter/PorterPage.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Button from '../../components/common/Button';
import PorterDispatch from '../../components/porter/PorterDispatch';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';

const PorterPage = () => {
  const { vendorProfile } = useAuth();
  const { showAlert } = useNotification();
  
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active'); // active, completed, all
  const [searchTerm, setSearchTerm] = useState('');
  const [showDispatchModal, setShowDispatchModal] = useState(false);

  // Dummy data - replace with Firestore integration later
  const dummyDeliveries = [
    {
      id: 'POR001',
      orderId: 'ORD123',
      customerName: 'John Doe',
      customerPhone: '+91 9876543210',
      pickupAddress: '123 Main St, Bengaluru, Karnataka 560001',
      dropAddress: '456 Park Ave, Bengaluru, Karnataka 560002',
      status: 'live',
      porterOrderId: 'PORTER_789',
      vehicleType: 'Bike',
      fare: 15000, // in paise
      createdAt: new Date('2024-06-06T10:30:00'),
      estimatedDelivery: new Date('2024-06-06T14:00:00'),
      partner: {
        name: 'Raj Kumar',
        phone: '+91 9876543210',
        vehicleNumber: 'KA01AB1234',
        rating: 4.5
      },
      trackingUrl: 'https://porter.in/track/PORTER_789'
    },
    {
      id: 'POR002',
      orderId: 'ORD124',
      customerName: 'Jane Smith',
      customerPhone: '+91 9876543211',
      pickupAddress: '789 Broadway, Bengaluru, Karnataka 560003',
      dropAddress: '321 Oak St, Bengaluru, Karnataka 560004',
      status: 'accepted',
      porterOrderId: 'PORTER_790',
      vehicleType: 'Mini Truck',
      fare: 25000,
      createdAt: new Date('2024-06-06T11:15:00'),
      estimatedDelivery: new Date('2024-06-06T15:30:00'),
      partner: {
        name: 'Suresh Babu',
        phone: '+91 9876543211',
        vehicleNumber: 'KA02CD5678',
        rating: 4.8
      },
      trackingUrl: 'https://porter.in/track/PORTER_790'
    },
    {
      id: 'POR003',
      orderId: 'ORD125',
      customerName: 'Mike Johnson',
      customerPhone: '+91 9876543212',
      pickupAddress: '555 First St, Bengaluru, Karnataka 560005',
      dropAddress: '777 Second St, Bengaluru, Karnataka 560006',
      status: 'ended',
      porterOrderId: 'PORTER_791',
      vehicleType: 'Bike',
      fare: 12000,
      createdAt: new Date('2024-06-05T09:00:00'),
      estimatedDelivery: new Date('2024-06-05T12:00:00'),
      deliveredAt: new Date('2024-06-05T11:45:00'),
      partner: {
        name: 'Arjun Singh',
        phone: '+91 9876543212',
        vehicleNumber: 'KA03EF9012',
        rating: 4.2
      },
      trackingUrl: 'https://porter.in/track/PORTER_791'
    },
    {
      id: 'POR004',
      orderId: 'ORD126',
      customerName: 'Sarah Wilson',
      customerPhone: '+91 9876543213',
      pickupAddress: '999 Third St, Bengaluru, Karnataka 560007',
      dropAddress: '111 Fourth St, Bengaluru, Karnataka 560008',
      status: 'open',
      porterOrderId: 'PORTER_792',
      vehicleType: 'Auto',
      fare: 18000,
      createdAt: new Date('2024-06-06T12:00:00'),
      estimatedDelivery: new Date('2024-06-06T16:00:00'),
      trackingUrl: 'https://porter.in/track/PORTER_792'
    },
    {
      id: 'POR005',
      orderId: 'ORD127',
      customerName: 'David Brown',
      customerPhone: '+91 9876543214',
      pickupAddress: '333 Fifth St, Bengaluru, Karnataka 560009',
      dropAddress: '444 Sixth St, Bengaluru, Karnataka 560010',
      status: 'cancelled',
      porterOrderId: 'PORTER_793',
      vehicleType: 'Bike',
      fare: 14000,
      createdAt: new Date('2024-06-05T15:30:00'),
      cancelledAt: new Date('2024-06-05T16:00:00'),
      cancelReason: 'Customer unavailable',
      trackingUrl: 'https://porter.in/track/PORTER_793'
    }
  ];

  // Load deliveries
  useEffect(() => {
    const loadDeliveries = async () => {
      try {
        setLoading(true);
        // TODO: Replace with actual Firestore call
        // const result = await getPorterDeliveries(vendorProfile.uid);
        
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        setDeliveries(dummyDeliveries);
        setLoading(false);
      } catch (error) {
        console.error('Error loading deliveries:', error);
        showAlert('Failed to load Porter deliveries', 'error');
        setLoading(false);
      }
    };

    if (vendorProfile?.uid) {
      loadDeliveries();
    }
  }, [vendorProfile]);

  // Filter deliveries based on active tab and search
  const filteredDeliveries = deliveries.filter(delivery => {
    const matchesTab = activeTab === 'all' || 
      (activeTab === 'active' && ['open', 'accepted', 'live'].includes(delivery.status)) ||
      (activeTab === 'completed' && ['ended', 'cancelled'].includes(delivery.status));
    
    const matchesSearch = searchTerm === '' || 
      delivery.orderId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      delivery.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      delivery.porterOrderId.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesTab && matchesSearch;
  });

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      open: { color: 'bg-yellow-100 text-yellow-800', label: 'Open' },
      accepted: { color: 'bg-orange-100 text-orange-800', label: 'Accepted' },
      live: { color: 'bg-blue-100 text-blue-800', label: 'In Transit' },
      ended: { color: 'bg-green-100 text-green-800', label: 'Delivered' },
      cancelled: { color: 'bg-red-100 text-red-800', label: 'Cancelled' },
      order_reopened: { color: 'bg-purple-100 text-purple-800', label: 'Reopened' }
    };

    const config = statusConfig[status] || { color: 'bg-gray-100 text-gray-800', label: status };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount / 100);
  };

  // Format date
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Handle dispatch success
  const handleDispatchSuccess = (result) => {
    showAlert(`Porter delivery created successfully! Tracking ID: ${result.order_id}`, 'success');
    setShowDispatchModal(false);
    // TODO: Refresh deliveries or add new delivery to state
  };

  // Handle dispatch error
  const handleDispatchError = (error) => {
    showAlert(`Failed to create Porter delivery: ${error}`, 'error');
  };

  // Refresh deliveries
  const refreshDeliveries = async () => {
    setLoading(true);
    // TODO: Reload from Firestore
    await new Promise(resolve => setTimeout(resolve, 1000));
    setLoading(false);
    showAlert('Deliveries refreshed', 'success');
  };

  // Get statistics
  const stats = {
    total: deliveries.length,
    active: deliveries.filter(d => ['open', 'accepted', 'live'].includes(d.status)).length,
    completed: deliveries.filter(d => d.status === 'ended').length,
    cancelled: deliveries.filter(d => d.status === 'cancelled').length
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Porter Deliveries</h1>
              <p className="mt-1 text-sm text-gray-600">
                Manage and track your Porter delivery orders
              </p>
            </div>
            <div className="flex space-x-3">
              <Button
                type="button"
                variant="secondary"
                onClick={refreshDeliveries}
                disabled={loading}
              >
                <div className="flex items-center">
                  <svg 
                    className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} 
                    xmlns="http://www.w3.org/2000/svg" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Refresh
                </div>
              </Button>
              <PorterDispatch
                order={{}} // Empty order for new dispatch
                onSuccess={handleDispatchSuccess}
                onError={handleDispatchError}
                showAlert={showAlert}
              />
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mt-6">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Total Deliveries</dt>
                    <dd className="text-lg font-medium text-gray-900">{stats.total}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-blue-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Active Deliveries</dt>
                    <dd className="text-lg font-medium text-gray-900">{stats.active}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-green-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Completed</dt>
                    <dd className="text-lg font-medium text-gray-900">{stats.completed}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-red-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Cancelled</dt>
                    <dd className="text-lg font-medium text-gray-900">{stats.cancelled}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="mt-6 bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
              {/* Tabs */}
              <div className="flex space-x-1">
                <button
                  className={`px-3 py-2 text-sm font-medium rounded-md ${
                    activeTab === 'active'
                      ? 'bg-orange-100 text-orange-700'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                  onClick={() => setActiveTab('active')}
                >
                  Active ({stats.active})
                </button>
                <button
                  className={`px-3 py-2 text-sm font-medium rounded-md ${
                    activeTab === 'completed'
                      ? 'bg-orange-100 text-orange-700'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                  onClick={() => setActiveTab('completed')}
                >
                  Completed ({stats.completed + stats.cancelled})
                </button>
                <button
                  className={`px-3 py-2 text-sm font-medium rounded-md ${
                    activeTab === 'all'
                      ? 'bg-orange-100 text-orange-700'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                  onClick={() => setActiveTab('all')}
                >
                  All ({stats.total})
                </button>
              </div>

              {/* Search */}
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                  </svg>
                </div>
                <input
                  type="text"
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="Search deliveries..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Deliveries Table */}
        <div className="mt-6 bg-white shadow overflow-hidden sm:rounded-md">
          {loading ? (
            <div className="p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
              <p className="mt-2 text-sm text-gray-500">Loading Porter deliveries...</p>
            </div>
          ) : filteredDeliveries.length === 0 ? (
            <div className="p-8 text-center">
              <svg className="mx-auto h-12 w-12 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 009.586 13H7" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No deliveries found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm ? 'Try adjusting your search criteria.' : 'Get started by creating your first Porter delivery.'}
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-200">
              {filteredDeliveries.map((delivery) => (
                <li key={delivery.id} className="px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            <div className="h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center">
                              <svg className="h-6 w-6 text-orange-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1v-5h2.038A2.968 2.968 0 0115 12.995V13a2.5 2.5 0 014.9 0H20a1 1 0 001-1v-5a4 4 0 00-4-4h-3.5V3a1 1 0 00-1-1H3z" />
                              </svg>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="flex items-center space-x-2">
                              <p className="text-sm font-medium text-gray-900">
                                Order #{delivery.orderId}
                              </p>
                              <span className="text-sm text-gray-500">•</span>
                              <p className="text-sm text-gray-500">
                                Porter ID: {delivery.porterOrderId}
                              </p>
                            </div>
                            <p className="text-sm text-gray-500">
                              {delivery.customerName} • {delivery.vehicleType} • {formatCurrency(delivery.fare)}
                            </p>
                            <div className="mt-1 flex items-center space-x-4 text-xs text-gray-500">
                              <span>Created: {formatDate(delivery.createdAt)}</span>
                              {delivery.estimatedDelivery && (
                                <span>ETA: {formatDate(delivery.estimatedDelivery)}</span>
                              )}
                              {delivery.deliveredAt && (
                                <span>Delivered: {formatDate(delivery.deliveredAt)}</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          {getStatusBadge(delivery.status)}
                        </div>
                      </div>

                      {/* Addresses */}
                      <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div className="bg-gray-50 p-3 rounded-md">
                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Pickup</p>
                          <p className="mt-1 text-sm text-gray-900">{delivery.pickupAddress}</p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-md">
                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Drop</p>
                          <p className="mt-1 text-sm text-gray-900">{delivery.dropAddress}</p>
                        </div>
                      </div>

                      {/* Partner Info */}
                      {delivery.partner && (
                        <div className="mt-3 bg-blue-50 p-3 rounded-md">
                          <p className="text-xs font-medium text-blue-700 uppercase tracking-wide">Delivery Partner</p>
                          <div className="mt-1 flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-blue-900">{delivery.partner.name}</p>
                              <p className="text-sm text-blue-700">{delivery.partner.vehicleNumber} • {delivery.partner.phone}</p>
                            </div>
                            <div className="flex items-center">
                              <svg className="h-4 w-4 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                              </svg>
                              <span className="ml-1 text-sm text-blue-700">{delivery.partner.rating}</span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Cancel Reason */}
                      {delivery.status === 'cancelled' && delivery.cancelReason && (
                        <div className="mt-3 bg-red-50 p-3 rounded-md">
                          <p className="text-xs font-medium text-red-700 uppercase tracking-wide">Cancellation Reason</p>
                          <p className="mt-1 text-sm text-red-900">{delivery.cancelReason}</p>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="ml-6 flex flex-col space-y-2">
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => window.open(delivery.trackingUrl, '_blank')}
                      >
                        <div className="flex items-center">
                          <svg className="h-4 w-4 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                          Track
                        </div>
                      </Button>
                      <Link to={`/orders/${delivery.orderId}`}>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="w-full"
                        >
                          View Order
                        </Button>
                      </Link>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default PorterPage;